import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api } from '../lib/api';
import { CheckCircle2, Linkedin } from 'lucide-react';

const AboutPage = () => {
  const [about, setAbout] = useState(null);

  useEffect(() => {
    api.get('/about').then((res) => setAbout(res.data)).catch(() => setAbout(null));
  }, []);

  if (!about) {
    return <div className="pt-40 pb-20 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="pt-28 pb-16 px-4 sm:px-6" data-testid="about-page">
      <div className="max-w-5xl mx-auto">
        {/* Hero */}
        <div className="grid md:grid-cols-5 gap-10 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-2"
          >
            <div className="relative rounded-3xl overflow-hidden glass-strong aspect-[4/5] glow-red">
              {about.photo_url ? (
                <img src={about.photo_url} alt={about.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#EF5B5B]/20 to-transparent">
                  <span className="text-7xl font-bold gradient-text">{about.name?.charAt(0) || 'J'}</span>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="md:col-span-3"
          >
            <span className="inline-block px-4 py-2 rounded-full glass text-sm text-gray-300 mb-5">{about.headline}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Meet <span className="gradient-text">{about.name}</span>
            </h1>
            <div className="prose prose-invert prose-p:text-gray-300 prose-p:leading-relaxed max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{about.bio}</ReactMarkdown>
            </div>
          </motion.div>
        </div>

        {/* Featured In / As Seen On */}
        <section className="mb-20" data-testid="featured-in-section" aria-label="Featured in press">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 rounded-full glass text-xs sm:text-sm text-gray-300 tracking-[0.25em] uppercase">
              As Seen On
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-5">
              Featured <span className="gradient-text">in the News</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mt-4">
              The conversations sparked on The AI Revolution Digest have reached front-page audiences —
              from LinkedIn's Top News to national coverage.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            {/* LinkedIn Today Top News */}
            <motion.a
              href="https://www.linkedin.com/newsletters/7106830033050079232/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="press-linkedin"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -6 }}
              className="group relative overflow-hidden rounded-3xl glass-strong border border-white/5 hover:border-[#0A66C2]/60 smooth p-8 flex flex-col items-center text-center"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#0A66C2]/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 smooth" />
              <div className="relative w-20 h-20 rounded-2xl bg-[#0A66C2] flex items-center justify-center mb-5 shadow-lg shadow-[#0A66C2]/30">
                <Linkedin className="w-10 h-10 text-white" strokeWidth={2.2} />
              </div>
              <span className="relative text-[10px] sm:text-xs uppercase tracking-[0.28em] text-[#0A66C2] font-semibold mb-2">
                LinkedIn
              </span>
              <h3 className="relative text-xl sm:text-2xl font-bold mb-2">
                Today's Top News
              </h3>
              <p className="relative text-sm text-gray-400 leading-relaxed max-w-sm">
                Interview reflections featured across LinkedIn's global news feed and top newsletter picks.
              </p>
              <span className="relative mt-5 inline-flex items-center gap-2 text-xs font-semibold text-[#EF5B5B] opacity-0 group-hover:opacity-100 smooth">
                View newsletter →
              </span>
            </motion.a>

            {/* USA News */}
            <motion.a
              href="https://usanews.com/newsroom/inside-the-ai-podcast-that-s-helping-shape-the-next-generation-of-business-leaders"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="press-usa-news"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              whileHover={{ y: -6 }}
              className="group relative overflow-hidden rounded-3xl glass-strong border border-white/5 hover:border-[#EF5B5B]/60 smooth p-8 flex flex-col items-center text-center"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#EF5B5B]/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 smooth" />
              <div className="relative w-full max-w-sm bg-white rounded-2xl px-4 py-3 mb-5 shadow-lg">
                <img
                  src="/press/usa-news.png"
                  alt="As Seen On USA News"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
              <span className="relative text-[10px] sm:text-xs uppercase tracking-[0.28em] text-[#EF5B5B] font-semibold mb-2">
                National Press
              </span>
              <h3 className="relative text-xl sm:text-2xl font-bold mb-2">
                Featured on USA News
              </h3>
              <p className="relative text-sm text-gray-400 leading-relaxed max-w-sm mb-6">
                Recognized among the standout voices shaping the conversation on AI, leadership, and the future of work.
              </p>
              <span
                data-testid="usa-news-read-feature-btn"
                className="relative inline-flex items-center gap-2 px-7 py-3 btn-red rounded-full text-sm font-bold glow-red"
              >
                Read the Feature
                <svg className="w-4 h-4 group-hover:translate-x-1 smooth" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </span>
            </motion.a>
          </div>
        </section>

        {/* Stats */}
        {about.stats && about.stats.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-20">
            {about.stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="glass-strong rounded-2xl p-8 text-center"
              >
                <div className="text-3xl sm:text-4xl font-bold gradient-text mb-2">{s.value}</div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">{s.label}</div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Why listen */}
        {about.why_listen && about.why_listen.length > 0 && (
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">
              Why <span className="gradient-text">Listen?</span>
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {about.why_listen.map((w, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="glass-strong rounded-2xl p-7 flex gap-4"
                >
                  <CheckCircle2 className="w-6 h-6 text-[#EF5B5B] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold mb-2">{w.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{w.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AboutPage;
